import React from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { UserRole } from '../../types/app';

export type AuthApi = {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ error?: string }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
};

const AuthContext = React.createContext<AuthApi | undefined>(undefined);

export function useAuth(): AuthApi {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [session, setSession] = React.useState<Session | null>(null);
  const [user, setUser] = React.useState<User | null>(null);
  const [role, setRole] = React.useState<UserRole | null>(null);

  const syncRole = React.useCallback(async (nextUser: User | null) => {
    if (!nextUser) {
      setRole(null);
      return;
    }
    const defaultRole = ((nextUser.user_metadata?.role as UserRole | undefined) ?? 'customer') as UserRole;
    try {
      const { data, error } = await supabase.from('users').select('role').eq('id', nextUser.id).maybeSingle();
      if (error) throw error;
      if (data?.role) {
        setRole(data.role as UserRole);
      } else {
        const { error: insertError } = await supabase
          .from('users')
          .upsert({ id: nextUser.id, email: nextUser.email ?? '', role: defaultRole });
        if (insertError) throw insertError;
        setRole(defaultRole);
      }
    } catch {
      setRole(defaultRole);
    }
  }, []);

  React.useEffect(() => {
    let unsub: (() => void) | undefined;

    async function boot() {
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
        await syncRole(data.session?.user ?? null);
        const { data: sub } = supabase.auth.onAuthStateChange((_evt, next) => {
          setSession(next);
          setUser(next?.user ?? null);
          void syncRole(next?.user ?? null);
        });
        unsub = () => sub.subscription.unsubscribe();
      } catch {
        setSession(null);
        setUser(null);
        setRole(null);
      } finally {
        setLoading(false);
      }
    }

    void boot();
    return () => unsub?.();
  }, [syncRole]);

  const value = React.useMemo<AuthApi>(
    () => ({
      user,
      session,
      role,
      loading,
      signUp: async (
        email: string,
        password: string,
        firstName: string,
        lastName: string
      ) => {
        try {
          const nextRole: UserRole = 'customer';
          const cleanFirst = firstName.trim();
          const cleanLast = lastName.trim();
          const displayName = [cleanFirst, cleanLast].filter(Boolean).join(' ');
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                role: nextRole,
                first_name: cleanFirst,
                last_name: cleanLast,
                display_name: displayName,
                full_name: displayName,
              },
            },
          });
          if (error) return { error: error.message };
          if (data.user) {
            const { error: upsertError } = await supabase
              .from('users')
              .upsert({ id: data.user.id, email: data.user.email ?? '', role: nextRole });
            if (upsertError) return { error: upsertError.message };
            setRole(nextRole);
          }
          return {};
        } catch {
          return { error: 'Sign up failed' };
        }
      },
      signIn: async (email: string, password: string) => {
        try {
          const { error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) return { error: error.message };
          return {};
        } catch {
          return { error: 'Sign in failed' };
        }
      },
      signOut: async () => {
        try {
          await supabase.auth.signOut();
          setRole(null);
        } catch {
          // ignore
        }
      },
    }),
    [loading, role, session, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

